import {
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ShoppingCart, Truck } from "lucide-react";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { formatBRL } from "../../lib/format";
import { sameSupplier, skuLinkClassName } from "../../lib/stock";

function formatBuyBy(ymd: string) {
  const [year, month, day] = ymd.split("-");
  return `${day}/${month}/${year}`;
}

export const Route = createFileRoute("/_app/purchases")({
  staticData: { layout: "wide" },
  component: PurchasesPage,
});

function PurchasesPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const report = useQuery({
    queryKey: ["purchases"],
    queryFn: async () => unwrap(await api.purchases.get()),
  });

  const createOrder = useMutation({
    mutationFn: async (supplier: string | null) =>
      unwrap(await api.purchases.orders.post({ supplier })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["purchases"] });
      toast.add({ type: "success", title: "Pedido criado" });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível criar o pedido",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  const receive = useMutation({
    mutationFn: async (id: string) =>
      unwrap(await api.purchases.orders({ id }).receive.post()),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["purchases"] });
      void queryClient.invalidateQueries({ queryKey: ["skus"] });
      toast.add({
        type: "success",
        title: "Recebido",
        description:
          "receivedUnits" in result
            ? `${result.receivedUnits} un. entraram no estoque${
                "costsUpdated" in result && result.costsUpdated > 0
                  ? ` · custo médio de ${result.costsUpdated} SKU${
                      result.costsUpdated === 1 ? "" : "s"
                    }`
                  : ""
              }.`
            : undefined,
      });
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível receber",
        description:
          error instanceof Error ? error.message : "Tente novamente.",
      });
    },
  });

  const orders = report.data && "orders" in report.data ? report.data.orders : [];
  const full = report.data && "full" in report.data ? report.data.full : null;

  return (
    <>
      <PageHeader
        title="Compras"
        description={
          report.data
            ? report.data.count > 0
              ? `${report.data.count} SKU${report.data.count === 1 ? "" : "s"} para repor`
              : orders.length > 0
                ? "Pedidos abertos — receba para entrar no saldo"
                : "Nada para comprar agora"
            : "O que pedir, quanto e até quando — pela velocidade de 30 dias"
        }
      />

      {report.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {full && full.count > 0 ? (
            <Card className="min-w-0 overflow-hidden">
              <div className="flex items-end justify-between gap-3 border-b border-line px-4 py-3">
                <div>
                  <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
                    Enviar ao Full
                  </p>
                  <p className="mt-0.5 font-mono text-2xl font-medium tracking-tight text-ink">
                    {full.units}
                    <span className="ml-1.5 text-[0.8125rem] font-normal text-ink-muted">
                      un.
                    </span>
                  </p>
                  <p className="mt-0.5 text-2xs text-ink-muted">
                    {full.items.some((row) => row.source === "ml")
                      ? "Sugestão oficial do Mercado Livre, com a velocidade local como reserva"
                      : "Velocidade dos anúncios Full · prazo de reposição do SKU"}
                  </p>
                </div>
                <Truck className="size-4 text-ink-faint" />
              </div>
              <ol className="divide-y divide-line">
                {full.items.map((row) => (
                  <li
                    key={row.skuId}
                    className="flex min-w-0 items-center gap-3 px-4 py-2.5"
                  >
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <Link
                        to="/stock/$id"
                        params={{ id: row.skuId }}
                        className={skuLinkClassName}
                      >
                        {row.code}
                      </Link>
                      <p className="truncate text-[0.8125rem] text-ink">
                        {row.title}
                      </p>
                      <p className="truncate text-2xs text-ink-muted">
                        Full {row.fullOnHand} · depósito {row.warehouseOnHand}
                        {" · "}
                        {row.source === "ml"
                          ? "sugestão do Mercado Livre"
                          : "pela sua velocidade de venda"}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-mono text-ink">{row.quantity} un.</p>
                      {row.overdue ? (
                        <Badge variant="warning">Hoje</Badge>
                      ) : (
                        <span className="font-mono text-2xs text-ink-muted">
                          {formatBuyBy(row.sendBy)}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          ) : null}

          {orders.map((order) => (
            <Card key={order.id} className="animate-rise">
              <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-2xs font-medium uppercase tracking-wider text-ink-muted">
                    A receber
                    {order.supplier ? ` · ${order.supplier}` : ""}
                  </p>
                  <p className="mt-0.5 font-mono text-2xl font-medium tracking-tight text-ink">
                    {order.remaining}
                    <span className="ml-1.5 text-[0.8125rem] font-normal text-ink-muted">
                      un.
                    </span>
                  </p>
                  <p className="mt-0.5 flex flex-wrap gap-x-2 text-2xs text-ink-muted">
                    {order.items.map((item) => (
                      <Link
                        key={item.id}
                        to="/stock/$id"
                        params={{ id: item.skuId }}
                        className={skuLinkClassName}
                      >
                        {item.code} × {item.quantity}
                      </Link>
                    ))}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={order.remaining <= 0}
                  loading={
                    receive.isPending && receive.variables === order.id
                  }
                  onClick={() => receive.mutate(order.id)}
                >
                  Receber no estoque
                </Button>
              </div>
            </Card>
          ))}

          {report.data && report.data.count > 0 ? (
            <>
              {report.data.groups.map((group) => {
                const alreadyOrdered = orders.some((order) =>
                  sameSupplier(order.supplier, group.supplier),
                );
                return (
                  <Card key={group.supplier ?? "none"}>
                    <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2">
                      <p className="text-2xs font-medium text-ink-muted">
                        {group.supplier ?? "Sem fornecedor"}
                      </p>
                      {alreadyOrdered ? (
                        <span className="text-2xs text-ink-muted">
                          Pedido aberto acima
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          variant="secondary"
                          loading={
                            createOrder.isPending &&
                            sameSupplier(
                              createOrder.variables,
                              group.supplier,
                            )
                          }
                          onClick={() => createOrder.mutate(group.supplier)}
                        >
                          Pedir
                        </Button>
                      )}
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>SKU</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead className="text-right">Comprar</TableHead>
                          <TableHead className="text-right">Até</TableHead>
                          <TableHead className="text-right">Em mãos</TableHead>
                          <TableHead className="text-right">Estimado</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.items.map((row) => (
                          <TableRow key={row.skuId}>
                            <TableCell>
                              <Link
                                to="/stock/$id"
                                params={{ id: row.skuId }}
                                className={skuLinkClassName}
                              >
                                {row.code}
                              </Link>
                            </TableCell>
                            <TableCell
                              className="min-w-0 max-w-md truncate"
                              title={row.title}
                            >
                              {row.title}
                            </TableCell>
                            <TableCell className="text-right font-mono text-ink">
                              {row.quantity}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.overdue ? (
                                <Badge variant="warning">Hoje</Badge>
                              ) : (
                                <span className="font-mono text-2xs text-ink-muted">
                                  {formatBuyBy(row.buyBy)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-ink-muted">
                              {row.onHand}
                            </TableCell>
                            <TableCell className="text-right font-mono text-ink-muted">
                              {row.cost != null
                                ? formatBRL(Number(row.cost) * row.quantity)
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                );
              })}
              <p className="text-2xs text-ink-muted">
                Pedir cria o pedido do grupo. Receber dá entrada no saldo e
                registra no histórico do SKU.
              </p>
            </>
          ) : orders.length === 0 && (!full || full.count === 0) ? (
            <Card>
              <EmptyState
                icon={<ShoppingCart />}
                title="Nada para comprar agora"
                description="A lista usa a velocidade de 30 dias e o prazo de reposição do SKU. Quando a cobertura chegar perto, aparece quanto comprar e até quando."
              />
            </Card>
          ) : null}
        </div>
      )}
    </>
  );
}
